import { Flex, Typography } from "antd";
import styled from "styled-components";
import { PreviewTitle } from "../../brandkit/components/preview/PreviewTitle";
import { ContactResponse, PresenterProfileResponse } from "../../types";
import { useAntTheme } from "hooks/useAntTheme/";
import { useMemo } from "react";
import { SliderVoicePreview } from "./SliderVoicePreview";
import {
  getContactName,
  getProfileNameWithCategory,
  getFaceProfileImagePath,
} from "./helpers";
import { AvatarImage } from "./AvatarImage";
import { useTranslate } from "#src/hooks";
import { radius } from "@libertify/ui";

export const AvatarSummary = ({
  contact,
  presenter,
}: {
  contact?: ContactResponse;
  presenter?: PresenterProfileResponse;
}) => {
  const t = useTranslate();

  const contactName = useMemo(() => getContactName(contact), [contact]);

  const faceProfileName = useMemo(
    () =>
      getProfileNameWithCategory(
        presenter?.face_profile?.name,
        presenter?.face_profile?.category
      ),
    [presenter]
  );

  const voiceProfileName = useMemo(() => {
    if (presenter?.voice_profile.category === "Cloned")
      return presenter.voice_profile.name;
    return getProfileNameWithCategory(
      presenter?.voice_profile.name,
      presenter?.voice_profile.category
    );
  }, [presenter]);

  const imagePath = getFaceProfileImagePath(presenter?.face_profile);

  const avatarItems = useMemo(
    () => [
      {
        title: t("components.ContactWithPresenter.AvatarSummary.name"),
        value: contactName,
      },
      ...(presenter?.face_profile
        ? [
            {
              title: t("components.ContactWithPresenter.AvatarSummary.face"),
              value: faceProfileName,
              preview: (
                <div style={{ width: 100 }}>
                  <AvatarImage src={imagePath ?? undefined} alt={contactName} />
                </div>
              ),
            },
          ]
        : []),
      {
        title: t("components.ContactWithPresenter.AvatarSummary.voice"),
        value: voiceProfileName,
        preview: presenter?.voice_profile && (
          <SliderVoicePreview
            voice={presenter.voice_profile}
            sliderWidth={100}
          />
        ),
      },
    ],
    [contactName, faceProfileName, imagePath, voiceProfileName, presenter, t]
  );

  return (
    <StyledAvatarContainer
      vertical
      gap={20}
      style={{ width: "30%" }}
      data-testid="avatar-summary-container"
    >
      <PreviewTitle
        title={t("components.ContactWithPresenter.AvatarSummary.title")}
      />
      {avatarItems.map((item) => (
        <AvatarSummaryItem key={item.title} {...item} />
      ))}
    </StyledAvatarContainer>
  );
};

const AvatarSummaryItem = ({
  title,
  value,
  preview,
}: {
  title: string;
  value: string;
  preview?: React.ReactNode;
}) => {
  const { theme } = useAntTheme();
  return (
    <Flex
      gap={20}
      style={{
        width: "100%",
        backgroundColor: theme.colorModalBgDesignSystem,
        borderRadius: radius.box,
        border: `1px solid ${theme.colorBorderSecondary}`,
        padding: 10,
      }}
      justify={"space-between"}
    >
      <Flex
        vertical
        gap={4}
        style={{ width: preview ? "50%" : "100%", height: "100%" }}
        justify="center"
      >
        <StyledSummaryText>{title}</StyledSummaryText>
        <StyledSummaryText style={{ fontWeight: 500, fontSize: 16 }}>
          {value}
        </StyledSummaryText>
      </Flex>
      {preview && <Flex style={{ maxWidth: "50%" }}>{preview}</Flex>}
    </Flex>
  );
};

export const StyledAvatarContainer = styled(Flex)`
  background-color: ${({ theme }) => theme.colorBgElevated};
  border-radius: ${radius.box}px;
  padding: 20px;
`;

const StyledSummaryText = styled(Typography.Text)`
  font-size: 18px;
  letter-spacing: -1px;
  line-height: 1.5;
`;
